import { parseNumericValue } from "./numberFormatter";
import {
  getZar8YieldAssetOption,
  ZAR8_YIELD_ASSET_OPTIONS,
} from "./zar8LockFlow";

export const ZAR8_YIELD_CALCULATOR_DURATION_OPTIONS = [
  {
    id: "6m",
    label: "6 Months",
    months: 6,
    multiplier: 1,
  },
  {
    id: "12m",
    label: "12 Months",
    months: 12,
    multiplier: 1.15,
  },
  {
    id: "3y",
    label: "3 Years",
    months: 36,
    multiplier: 1.35,
  },
  {
    id: "5y",
    label: "5 Years",
    months: 60,
    multiplier: 1.6,
  },
];

export const DEFAULT_ZAR8_YIELD_CALCULATOR_ASSET_ID =
  ZAR8_YIELD_ASSET_OPTIONS[0]?.id || "btc";
export const DEFAULT_ZAR8_YIELD_CALCULATOR_DURATION_ID =
  ZAR8_YIELD_CALCULATOR_DURATION_OPTIONS[0]?.id || "6m";

const CALCULATOR_EVENT_PRODUCT = "zar8_yield";

export const getZar8YieldCalculatorDurationOption = (durationId) =>
  ZAR8_YIELD_CALCULATOR_DURATION_OPTIONS.find(
    (option) => option.id === durationId
  ) || null;

export const sanitizeZar8YieldCalculatorAmountInput = (
  value,
  amountMode = "zar"
) => {
  const normalizedValue = `${value || ""}`.replace(/[^0-9.]/g, "");
  const [wholePart, ...decimalParts] = normalizedValue.split(".");
  const maxDecimals = amountMode === "zar8" ? 8 : 2;
  const decimalPart = decimalParts.join("").slice(0, maxDecimals);

  if (decimalPart.length > 0) {
    return `${wholePart}.${decimalPart}`;
  }

  return wholePart;
};

export const getZar8YieldCalculatorAssetLabel = (assetId) => {
  const assetOption = getZar8YieldAssetOption(assetId);

  if (!assetOption) {
    return "BTC";
  }

  if (assetOption.id === "xaut") {
    return "XAUT";
  }

  return assetOption.label;
};

export const getZar8YieldCalculatorAvailableBalanceZar = (asset) => {
  const explicitValue = parseNumericValue(
    asset?.valueInLocalCurrency ?? asset?.valueInZar
  );

  if (explicitValue > 0) {
    return explicitValue;
  }

  return parseNumericValue(asset?.availableAmount);
};

export const getZar8YieldCalculatorZar8Price = (asset) => {
  const availableAmount = parseNumericValue(asset?.availableAmount);
  const availableValueZar = getZar8YieldCalculatorAvailableBalanceZar(asset);

  if (availableAmount > 0 && availableValueZar > 0) {
    return availableValueZar / availableAmount;
  }

  return 1;
};

export const getZar8YieldCalculatorAssetPriceInZar = (
  marketAssets,
  assetId
) => {
  if (!marketAssets) {
    return 0;
  }

  let matchedEntry = null;

  if (assetId === "btc") {
    matchedEntry = marketAssets.BTC || marketAssets.btc || null;
  } else if (assetId === "xaut") {
    matchedEntry = marketAssets.XAUT || marketAssets.xaut || null;
  } else {
    const assetOption = getZar8YieldAssetOption(assetId);
    const assetSymbol = assetOption?.label;

    if (!assetSymbol) {
      return 0;
    }

    matchedEntry =
      marketAssets[assetSymbol.toUpperCase()] ||
      marketAssets[assetSymbol.toLowerCase()] ||
      null;
  }

  return parseNumericValue(
    matchedEntry?.priceInZar ??
      matchedEntry?.info?.zarprice ??
      matchedEntry?.price
  );
};

export const calculateZar8YieldCalculatorProjection = ({
  amountInput,
  amountMode = "zar",
  assetId,
  durationId,
  assetPriceInZar = 0,
  zar8PriceInZar = 1,
}) => {
  const rawAmount = parseNumericValue(amountInput);
  const assetOption = getZar8YieldAssetOption(assetId);
  const durationOption = getZar8YieldCalculatorDurationOption(durationId);
  const normalizedZar8Price = zar8PriceInZar > 0 ? zar8PriceInZar : 1;
  const amountZar =
    amountMode === "zar8" ? rawAmount * normalizedZar8Price : rawAmount;
  const amountZar8 =
    amountMode === "zar8" ? rawAmount : amountZar / normalizedZar8Price;

  if (!assetOption || !durationOption || amountZar <= 0) {
    return {
      annualYieldRate: 0,
      estimatedRewardsZar: 0,
      estimatedAssetEarned: 0,
      principalAssetEquivalent: 0,
      principalZar: amountZar,
      principalZar8: amountZar8,
      durationMonths: durationOption?.months || 0,
    };
  }

  const annualYieldRate = assetOption.apr * durationOption.multiplier;
  const estimatedRewardsZar =
    amountZar * annualYieldRate * (durationOption.months / 12);
  const estimatedAssetEarned =
    assetPriceInZar > 0 ? estimatedRewardsZar / assetPriceInZar : 0;
  const principalAssetEquivalent =
    assetPriceInZar > 0 ? amountZar / assetPriceInZar : 0;

  return {
    annualYieldRate,
    estimatedRewardsZar,
    estimatedAssetEarned,
    principalAssetEquivalent,
    principalZar: amountZar,
    principalZar8: amountZar8,
    durationMonths: durationOption.months,
  };
};

export const emitZar8YieldCalculatorEvent = (eventName, payload = {}) => {
  if (typeof window === "undefined") {
    return;
  }

  const eventPayload = {
    product: CALCULATOR_EVENT_PRODUCT,
    ...payload,
  };

  if (window.analytics?.track) {
    window.analytics.track(eventName, eventPayload);
  }

  if (Array.isArray(window.dataLayer)) {
    window.dataLayer.push({
      event: eventName,
      ...eventPayload,
    });
  }

  window.dispatchEvent(
    new CustomEvent("zar8-yield-calculator", {
      detail: {
        eventName,
        payload: eventPayload,
      },
    })
  );
};
